import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { ButtonGroup, Popper } from '@mui/material';
import { isEmpty } from 'lodash';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useCompanyDashboardContext } from 'dashboard409a/context/CompanyDashboardContext/CompanyDashboardContext';
import { StyledButton, StyledButtonTrigger, StyledLockButton, StyledMenu } from './ActionButtons.styles';
import MenuOption from './MenuOption';
import { ActionProps, PageActionsProps } from './types';

const tabBarId = 'test';

const ActionButtons = () => {
  const companyDashboardContext = useCompanyDashboardContext();

  const pageActions = companyDashboardContext.pageActions as PageActionsProps | undefined;
  const { mainAction, secondaryActions = [] } = pageActions || { mainAction: undefined, secondaryActions: [] };
  const [anchorEl, setAnchorEl] = useState<null | (EventTarget & HTMLElement)>(null);
  const open = Boolean(anchorEl);
  const [{ isShowLoadingProgress = false }] = useStore() as unknown as UseStoreValues;

  const activeOptions = useMemo(
    () => (secondaryActions ? secondaryActions.filter((option: ActionProps) => option.isActive) : []),
    [secondaryActions]
  );

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleSecondaryItemClick = useCallback((option: ActionProps) => {
    setAnchorEl(null);
    option.callback();
  }, []);

  return (
    <>
      <ButtonGroup color="secondary" aria-label="Page actions">
        {mainAction && (
          <>
            {mainAction.isLock ? (
              <StyledLockButton
                color="secondary"
                disabled={mainAction.isActive || isShowLoadingProgress}
                id={`${mainAction?.id || tabBarId}-main-action`}
                onClick={mainAction.callback}
                variant="contained">
                {mainAction.label}
              </StyledLockButton>
            ) : (
              <StyledButton
                color="secondary"
                disabled={mainAction.isActive || isShowLoadingProgress}
                id={`${mainAction?.id || tabBarId}-main-action`}
                onClick={mainAction.callback}
                variant="contained">
                {mainAction.label}
              </StyledButton>
            )}
          </>
        )}
        {!isEmpty(activeOptions) && (
          <>
            {mainAction?.isLock ? (
              <StyledLockButton
                color="secondary"
                disabled={isShowLoadingProgress}
                id={`${mainAction?.id || tabBarId}-dropdown-action`}
                isDropdownAction
                onClick={handleClick}
                variant="contained">
                <ArrowDropDownIcon />
              </StyledLockButton>
            ) : (
              <StyledButtonTrigger
                color="secondary"
                disabled={isShowLoadingProgress}
                id={`${mainAction?.id || tabBarId}-dropdown-action`}
                onClick={handleClick}
                variant="contained">
                <ArrowDropDownIcon />
              </StyledButtonTrigger>
            )}
          </>
        )}
      </ButtonGroup>

      <Popper open={open} anchorEl={anchorEl} id={open ? 'transitions-popper' : undefined}>
        <StyledMenu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}>
          {activeOptions.map((option: ActionProps, optionIndex: number) => (
            <MenuOption
              key={`${mainAction?.id ?? tabBarId}-action-item-${optionIndex + 1}`}
              mainAction={mainAction as ActionProps}
              tabBarId={tabBarId}
              option={option}
              optionIndex={optionIndex}
              onClick={handleSecondaryItemClick}
            />
          ))}
        </StyledMenu>
      </Popper>
    </>
  );
};

export default ActionButtons;
